import { supabase } from './supabase';
import type { ProjectShareContent } from '../types/projects';

export type ProjectShareStatus = 'draft' | 'shared' | 'changes_requested' | 'approved';
export type ProjectFeedbackKind = 'comment' | 'change_request' | 'approval';

export type ProjectShareLineItem = {
  name: string;
  description: string;
  quantity: number;
  unit_price: number;
  tax_rate: number | null;
  total: number;
};

export type ProjectShareQuote = {
  id: string;
  quote_number: string | null;
  amount: number;
  currency: string;
  status: string | null;
};

export type ProjectShareBranding = {
  studioName: string;
  legalName: string;
  contactEmail: string;
  phone: string;
  addressLine1: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  taxId: string;
  currency: string;
  primaryColor: string;
  logoUrl?: string;
};

export type ProjectShareFeedback = {
  id: number;
  kind: ProjectFeedbackKind;
  body: string;
  author_name: string;
  author_email: string;
  created_at: string;
};

export type ProjectShareBrief = {
  token: string | null;
  shareUrl: string | null;
  shareStatus: ProjectShareStatus;
  project: { id: number; name: string; description: string | null };
  client: {
    name: string;
    contact: string;
    email: string;
    phone: string;
    address: string;
    locale: 'en' | 'es';
  };
  brief: Partial<ProjectShareContent> | null;
  quote: ProjectShareQuote | null;
  lineItems: ProjectShareLineItem[];
  contractText: string;
  branding: ProjectShareBranding;
  feedback: ProjectShareFeedback[];
  approved: { at: string | null; name: string | null; email: string | null } | null;
};

const SHARE_PATH = '/api/project-share';

async function parseJson(response: Response) {
  const type = response.headers.get('content-type') || '';
  if (!type.includes('application/json')) {
    throw new Error('Project share request failed');
  }
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || 'Project share request failed');
  }
  return payload;
}

function asBrief(payload: Record<string, unknown>): ProjectShareBrief {
  if (!payload || typeof payload.shareStatus !== 'string' || !payload.project) {
    throw new Error('Project share request failed');
  }
  return payload as unknown as ProjectShareBrief;
}

export async function fetchProjectShare(token: string): Promise<ProjectShareBrief> {
  const response = await fetch(`${SHARE_PATH}?token=${encodeURIComponent(token)}`);
  return asBrief(await parseJson(response));
}

export async function submitProjectShare(input: {
  action: 'comment' | 'requestChanges' | 'approve';
  token: string;
  name: string;
  email: string;
  body?: string;
}): Promise<ProjectShareBrief> {
  const response = await fetch(SHARE_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return asBrief(await parseJson(response));
}

async function staffRequest(body: Record<string, unknown>) {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Authentication required');
  const response = await fetch(SHARE_PATH, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  return parseJson(response);
}

export async function issueProjectShareLink(projectId: number): Promise<{
  token: string;
  shareUrl: string;
  shareStatus: ProjectShareStatus;
}> {
  const payload = await staffRequest({ action: 'issueLink', projectId });
  if (!payload?.token || !payload?.shareUrl) {
    throw new Error(payload.error || 'Could not create share link');
  }
  return payload as { token: string; shareUrl: string; shareStatus: ProjectShareStatus };
}

export async function reopenProjectShare(projectId: number): Promise<ProjectShareBrief> {
  return asBrief(await staffRequest({ action: 'reopen', projectId }));
}

export function projectShareUrl(token: string | null | undefined) {
  if (!token) return null;
  if (typeof window === 'undefined') return `/p/${token}`;
  return `${window.location.origin}/p/${token}`;
}
